"use client";

import { stegaClean } from "@sanity/client/stega";
import { motion, type Transition, useInView, type UseInViewOptions } from "motion/react";
import Link from "next/link";
import * as React from "react";

import CustomPortableText, {
  portableTextLinkClassName,
  type PortableTextValue,
} from "@/components/shared/portable-text";
import { getLogger } from "@/lib/logger";
import { cn } from "@/lib/ui/cn";
import { isExternalUrl } from "@/lib/url";

const log = getLogger("WritingText");

type WordToken = { word: string; href?: string; blank?: boolean };

function isPortableText(value: unknown): value is PortableTextValue {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === "object" &&
    value[0] !== null &&
    ("_type" in value[0] || "children" in value[0])
  );
}

/**
 * Parses Portable Text into word-level tokens, preserving link marks and block boundaries.
 * Each token is either plain text or has href/blank for link wrapping.
 * Returns an array of blocks, where each block is an array of tokens (or null for empty blocks).
 * Empty blocks are preserved to maintain multiple line breaks.
 * Only handles block/span structure; returns [] for other block types (fallback to CustomPortableText).
 */
function portableTextToWordTokens(blocks: PortableTextValue): (WordToken[] | null)[] {
  const blockTokens: (WordToken[] | null)[] = [];
  const arr = Array.isArray(blocks) ? blocks : [];

  for (const block of arr) {
    const b = block as { _type?: string; children?: unknown[]; markDefs?: unknown[] };
    if (b._type !== "block" || !Array.isArray(b.children)) continue;

    const markDefs = (b.markDefs ?? []) as {
      _key?: string;
      _type?: string;
      href?: string;
      blank?: boolean;
    }[];
    const linkDefs = new Map<string, { href: string; blank?: boolean }>();
    for (const m of markDefs) {
      if (m._type === "link" && m.href != null && typeof m._key === "string") {
        linkDefs.set(m._key, { href: m.href, blank: m.blank });
      }
    }

    const tokens: WordToken[] = [];
    for (const child of b.children) {
      const c = child as { _type?: string; text?: string; marks?: string[] };
      if (c._type !== "span" || typeof c.text !== "string") continue;

      const cleaned = String(stegaClean(c.text) ?? c.text ?? "");
      const words = cleaned.split(/\s+/).filter(Boolean);
      let link: { href: string; blank?: boolean } | undefined;
      for (const key of c.marks ?? []) {
        const def = linkDefs.get(key);
        if (def) {
          link = def;
          break;
        }
      }

      for (const word of words) {
        tokens.push({ word, href: link?.href, blank: link?.blank });
      }
    }

    // Preserve empty blocks (null) to maintain multiple line breaks
    if (tokens.length > 0) {
      blockTokens.push(tokens);
    } else {
      blockTokens.push(null);
    }
  }

  return blockTokens;
}

type WritingTextProps = Omit<React.ComponentProps<"span">, "children"> & {
  transition?: Transition;
  inView?: boolean;
  inViewMargin?: UseInViewOptions["margin"];
  inViewOnce?: boolean;
  spacing?: number | string;
  text: string | PortableTextValue;
};

function WritingText({
  ref,
  inView = false,
  inViewMargin = "0px",
  inViewOnce = true,
  spacing = 5,
  text,
  transition = { type: "spring", bounce: 0, duration: 2, delay: 0.5 },
  ...props
}: WritingTextProps) {
  const localRef = React.useRef<HTMLSpanElement>(null);
  React.useImperativeHandle(ref, () => localRef.current as HTMLSpanElement);

  const inViewResult = useInView(localRef, {
    once: inViewOnce,
    margin: inViewMargin,
  });
  const isInView = !inView || inViewResult;

  const words = React.useMemo(() => {
    if (isPortableText(text)) {
      return null;
    }
    if (!text?.split) {
      log.warn("text is not a string", { text });
    }
    return String(stegaClean(text ?? "") ?? "")
      .split(/\s+/)
      .filter(Boolean);
  }, [text]);

  const ptBlockTokens = React.useMemo(
    () => (isPortableText(text) ? portableTextToWordTokens(text) : []),
    [text],
  );

  if (isPortableText(text)) {
    if (ptBlockTokens.length > 0) {
      let globalTokenIndex = 0;
      return (
        <span ref={localRef} data-slot="writing-text" {...props}>
          {ptBlockTokens.map((blockTokens, blockIndex) => {
            // Handle empty blocks (null) - render as line break with spacing
            if (blockTokens === null) {
              return (
                <React.Fragment key={`empty-${blockIndex}`}>
                  <br />
                  <span
                    className="inline-block"
                    style={{ width: 0, height: "1rem" }}
                    aria-hidden="true"
                  />
                </React.Fragment>
              );
            }

            // Handle blocks with content
            return (
              <React.Fragment key={blockIndex}>
                {blockIndex > 0 && (
                  <>
                    <br />
                    <span
                      className="inline-block"
                      style={{ width: 0, height: "1rem" }}
                      aria-hidden="true"
                    />
                  </>
                )}
                {blockTokens.map((token) => {
                  const currentIndex = globalTokenIndex++;
                  return (
                    <motion.span
                      key={currentIndex}
                      className="will-change-opacity inline-block will-change-transform"
                      style={{ marginRight: spacing }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={isInView ? { opacity: 1, y: 0 } : undefined}
                      transition={{
                        ...transition,
                        delay: currentIndex * (transition?.delay ?? 0),
                      }}
                    >
                      {token.href && token.href.trim() !== "" ? (
                        <Link
                          href={token.href}
                          className={portableTextLinkClassName}
                          rel={
                            token.blank || isExternalUrl(token.href)
                              ? "noopener noreferrer"
                              : undefined
                          }
                          target={token.blank || isExternalUrl(token.href) ? "_blank" : undefined}
                        >
                          {token.word}
                        </Link>
                      ) : (
                        token.word
                      )}{" "}
                    </motion.span>
                  );
                })}
              </React.Fragment>
            );
          })}
        </span>
      );
    }

    return (
      <motion.span
        ref={localRef}
        data-slot="writing-text-portable"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : undefined}
        transition={{ duration: 0.5 }}
        style={props.style}
        id={props.id}
        className={cn("writing-text-portable", props.className)}
      >
        <CustomPortableText
          value={text}
          textClassName={cn("writing-text-portable", props.className)}
        />
      </motion.span>
    );
  }

  return (
    <span ref={localRef} data-slot="writing-text" {...props}>
      {words?.map((word, index) => (
        <motion.span
          key={index}
          className="will-change-opacity inline-block will-change-transform"
          style={{ marginRight: spacing }}
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : undefined}
          transition={{
            ...transition,
            delay: index * (transition?.delay ?? 0),
          }}
        >
          {word}{" "}
        </motion.span>
      ))}
    </span>
  );
}

export { WritingText, type WritingTextProps };
